import {
    Component,
    EventEmitter,
    forwardRef,
    inject,
    Input,
    OnInit,
    Output,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { firstValueFrom } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ButtonStackComponent } from "../../../../button-stack/button-stack.component";
import { ButtonComponent } from "../../../../button/button.component";
import { SelectorComponent } from "../../../../selector/selector.component";
import { IMapComponent } from "../../../map.models";
import { IMapDrawLayer, IMapGeometry, IMapGeometryWicket, IWidgetMenuButton } from "../../../map.settings";
import { MapSketch2Service } from "../../../services/map-sketch2.service";
import { SpatialService } from "../../../services/spatial.service";
import { MapBaseSubComponent } from "../map-base-sub-component/map-base-sub.component";
import { IMapExtentConfig } from "./map-extent-config";
import { IForeslagetKartutsnitt, IKartutsnitt, KartutsnittTyp } from "./map-extent.models";
import { MapExtentService } from "./map-extent.service";
import { MapExtentTexts } from "./map-extent.texts";

@Component({
    selector: "rask-map-extent",
    templateUrl: "./map-extent.component.html",
    styleUrls: ["./map-extent.component.scss"],
    providers: [
        {
            provide: MapBaseSubComponent,
            useExisting: forwardRef(() => MapExtentComponent)
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonStackComponent,
        ButtonComponent,
        SelectorComponent,
        FormsModule
    ]
})
export class MapExtentComponent extends MapBaseSubComponent implements OnInit {

    get customScaleText(): string {
        if (!this.selectedExtent) {
            return "";
        }
        return this.scaleViewFormatter(this.selectedExtent.skala);
    }

    @Input()
    set enabled(value: boolean) {
        // eslint-disable-next-line sonarjs/no-selector-parameter
        if (value && !this.isEnabled) {
            this.init();
            this.isEnabled = true;
        } else if (!value && this.isEnabled) {
            this.clear();
            this.isEnabled = false;
        }
    }
    get enabled() {
        return this.isEnabled;
    }

    texts = MapExtentTexts.texts;
    scales: number[] = [
        100000,
        75000,
        50000,
        30000,
        20000,
        10000,
        9000,
        8000,
        7000,
        6000,
        5000,
        4000,
        3000,
        2000,
        1000
    ];
    extents: IForeslagetKartutsnitt;
    @Input() config: IMapExtentConfig;
    @Output() extentUpdated = new EventEmitter<IKartutsnitt>();
    @Output() extentRemoved = new EventEmitter<IKartutsnitt>();

    widgetId = "map-extent";
    componentReady = false;

    selectedScale: number;
    selectedExtent: IKartutsnitt;

    isVisible = false;
    isEnabled = false;
    isCreating = false;
    view: any;
    draw: any;
    map: IMapComponent;
    buttons: IWidgetMenuButton[] = [
        {
            iconClass: "fa-vector-square",
            tooltipText: "Kartutsnitt",
            visible: () => this.enabled && !!this.config?.arbetsorderId,
            widgetId: this.widgetId
        }
    ] as IWidgetMenuButton[];

    private currentEdit: IMapGeometry = null;
    private readonly defaultScale = 2000;
    private layer: IMapDrawLayer;

    private readonly spatialService = inject(SpatialService);
    private readonly mapExtentService = inject(MapExtentService);
    private readonly mapSketch2Service = inject(MapSketch2Service);

    onInit(view: any, draw: any): void {
        this.draw = draw;
        this.view = view;
        this.componentReady = true;
    }

    ngOnInit(): void {
        this.createDrawLayer();
        this.onShow.pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.map?.setLayerFocus([this.widgetId]);
            this.isVisible = true;
            this.init();
        });
        this.onHide.pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => {
            if (this.isCurrentlyEditing()) {
                this.mapSketch2Service.cancel();
            }
            this.isVisible = false;
            this.clear();
            this.map?.clearLayerFocus();
        });
        this.mapReady.pipe(
            takeUntil(this.destroy$)
        ).subscribe(m => this.map = m);
    }

    scaleViewFormatter = (r) => `1: ${r}`;
    canChangeScale = () => this.config.canEdit && this.selectedExtent;
    scaleChanged = (e) => {
        if (!this.canChangeScale() || isNaN(e)) {
            return;
        }
        this.mapSketch2Service.complete();
        this.mapExtentService.resizeKartutsnittToScale(this.selectedExtent, e);
        this.updateExtentOnLayer(this.selectedExtent);
        this.map.refreshGeometries(this.layer.id);
        this.extentUpdated.emit(this.selectedExtent);
        const geometry = this.layer.geometries.find(x => x.id === this.selectedExtent.guid);
        this.startEdit(geometry);
    };

    canCalculateExtents = () => this.config.canEdit;
    calculateExtents = () => {
        if (this.isCurrentlyEditing()) {
            this.mapSketch2Service.cancel();
        }
        this.selectExtent(null);
        this.mapExtentService.getExtentSuggestions(this.config.arbetsorderId).subscribe(extentSuggestions => {
            if (this.extents?.oversiktskarta1) {
                this.deleteAllDetaljkartor();
                extentSuggestions.oversiktskarta1.id = this.extents.oversiktskarta1.id;
                extentSuggestions.oversiktskarta1.guid = this.extents.oversiktskarta1.guid;
                extentSuggestions.oversiktskarta2.id = this.extents.oversiktskarta2.id;
                extentSuggestions.oversiktskarta2.guid = this.extents.oversiktskarta2.guid;
                this.clear();
            }
            this.pushExtentSuggestionEvents(extentSuggestions);
            this.setExtents(extentSuggestions);
        });
    };

    canDeleteSelectedExtent = () => this.config.canEdit && this.selectedExtent && this.selectedExtent?.typ === KartutsnittTyp.Detaljkarta;
    deleteSelectedExtent = () => {
        this.removeGeometry(this.selectedExtent);
        this.removeExtent(this.selectedExtent);
        this.extentRemoved.emit(this.selectedExtent);
        this.selectExtent(null);
    };

    canCreateNewExtent = () => this.config.canEdit && !this.selectedExtent && !!this.extents;
    createNewExtent = () => {
        const detaljKartutsnitt = this.mapExtentService.createNewKartutsnitt(this.defaultScale, this.view.extent, this.extents);
        this.extents.detaljkartor.push(detaljKartutsnitt);
        this.addExtentToLayer(detaljKartutsnitt);
        this.map.refreshGeometries(this.layer.id);
        this.extentUpdated.emit(detaljKartutsnitt);
    };

    isCurrentlyEditing() {
        return ObjectUtils.hasValue(this.currentEdit);
    }

    clickExtentLayer(mouse: any, geometry: IMapGeometry) {
        if (ObjectUtils.isNullOrUndefined(geometry)) {
            return;
        }
        if (this.isCurrentlyEditing()) {
            this.finishEditCurrent(geometry);
        } else {
            this.startEdit(geometry);
        }
    }

    startEdit(clickedGeometry: IMapGeometry) {
        this.mapSketch2Service.edit(this.layer.id, clickedGeometry, (completedGeometry, aborted, info) => {
            this.completed(completedGeometry, aborted, info);
        });
        this.currentEdit = clickedGeometry;
        this.selectExtent(clickedGeometry.tag);
    }

    finishEditCurrent(clickedGeometry: IMapGeometry) {
        const completedGeometry = this.mapSketch2Service.complete(clickedGeometry as IMapGeometryWicket);
        this.currentEdit = null;
        this.selectExtent(null);
        this.map.reorderPolygonsBySize(this.layer.id);

        if (ObjectUtils.hasValue(clickedGeometry) && clickedGeometry.id !== completedGeometry?.id) {
            this.startEdit(clickedGeometry);
        }
    }

    completed(geometry: IMapGeometry, aborted, info) {
        const kartutsnitt = this.layer.geometries.find(x => x.id === geometry.id).tag as IKartutsnitt;
        this.mapExtentService.updateKartutsnittFromWkt(kartutsnitt, (geometry as IMapGeometryWicket).wkt);
        this.extentUpdated.emit(kartutsnitt);
    }

    private init() {
        if (this.componentReady && this.isVisible && !this.extents) {
            this.getExtents();
        }
    }

    private clear() {
        this.layer.geometries = [];
        this.map?.refreshGeometries(this.layer.id);
        this.extents = undefined;
        this.selectExtent(null);
    }

    private deleteAllDetaljkartor() {
        this.extents.detaljkartor?.forEach(kartutsnitt => {
            this.extentRemoved.emit(kartutsnitt);
        });
        this.extents.detaljkartor = [];
    }

    private createDrawLayer() {
        this.layer = {
            id: this.widgetId,
            geometries: [],
            title: "",
            showGeometriesMenu: true,
            readonly: true,
            visible: true,
            selectable: true,
            hideInLayerList: true,
            onClick: (mouse, geometry) => {
                this.clickExtentLayer(mouse, geometry);
                return false;
            }
        };
        this.drawLayers.push(this.layer);
    }

    private findGeometry(kartutsnitt: IKartutsnitt) {
        if (ObjectUtils.hasValue(kartutsnitt.guid)) {
            return this.layer.geometries.find(x => x.tag.guid === kartutsnitt.guid);
        }
        // nytt kartutsnitt utan guid
        return this.layer.geometries.find(x => x.tag === kartutsnitt);
    }

    private removeGeometry(kartutsnitt: IKartutsnitt) {
        if (this.isCurrentlyEditing()) {
            this.mapSketch2Service.cancel();
        }
        const geometry = this.findGeometry(kartutsnitt);
        const index = this.layer.geometries.indexOf(geometry);
        if (index > -1) {
            this.layer.geometries.splice(index, 1);
        }
        this.map.refreshGeometries(this.layer.id);
    }

    private removeExtent(kartutsnitt: IKartutsnitt) {
        const index = this.extents.detaljkartor.indexOf(kartutsnitt);
        if (index > -1) {
            this.extents.detaljkartor.splice(index, 1);
        }
    }

    private selectExtent(extent: IKartutsnitt) {
        if (extent) {
            this.selectedExtent = extent;
            this.selectedScale = undefined;
        } else {
            this.selectedExtent = null;
            this.selectedScale = this.defaultScale;
        }
    }

    private pushExtentSuggestionEvents(foreslagetKartutsnitt: IForeslagetKartutsnitt) {
        this.extentUpdated.emit(foreslagetKartutsnitt.oversiktskarta1);
        this.extentUpdated.emit(foreslagetKartutsnitt.oversiktskarta2);
        foreslagetKartutsnitt.detaljkartor.forEach(extent => this.extentUpdated.emit(extent));
    }

    private addSuggestionsToLayer(foreslagetKartutsnitt: IForeslagetKartutsnitt) {
        this.addExtentToLayer(foreslagetKartutsnitt.oversiktskarta1);
        this.addExtentToLayer(foreslagetKartutsnitt.oversiktskarta2);
        foreslagetKartutsnitt.detaljkartor.forEach(e => this.addExtentToLayer(e));
        this.map.refreshGeometries(this.layer.id);
        this.map.gotoAll([this.layer.id]);
    }

    private updateExtentOnLayer(extent: IKartutsnitt) {
        const existing = this.findGeometry(extent) as IMapGeometryWicket;
        existing.wkt = this.spatialService.extentToWktPolygon(extent.extent);
        existing.tag.skala = extent.skala;
    }

    private addExtentToLayer(extent: IKartutsnitt) {
        const mapGeometry = this.mapExtentService.getGeometryWicketFromExtent(extent);
        this.layer.geometries.push(mapGeometry);
    }

    private async getExtents() {
        const result = await firstValueFrom(this.mapExtentService.getExtentsForArbetsorder(this.config.arbetsorderId));
        if (result) {
            this.setExtents(result);
            return;
        }

        if (this.config.canEdit) {
            this.calculateExtents();
        }
    }

    private setExtents(kartutsnitt: IForeslagetKartutsnitt) {
        this.extents = kartutsnitt;
        this.addSuggestionsToLayer(kartutsnitt);
    }
}
